import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { MapleMarketApp } from '@/data/mapleMarketApps';

interface DescriptionTabProps {
  product: MapleMarketApp;
  disabled: boolean;
  onSaved: () => Promise<void> | void;
}

export default function DescriptionTab({ product, disabled, onSaved }: DescriptionTabProps) {
  const { toast } = useToast();
  const [shortDescription, setShortDescription] = useState(product.description);
  const [longDescription, setLongDescription] = useState(product.longDescription);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setShortDescription(product.description);
    setLongDescription(product.longDescription);
  }, [product]);

  const handleSave = async () => {
    if (!shortDescription.trim() || !longDescription.trim()) {
      toast({
        title: 'Description required',
        description: 'Both short and long descriptions are required.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    const { error } = await (supabase.from('marketplace_products') as any)
      .update({
        short_description: shortDescription.trim(),
        long_description: longDescription.trim(),
      })
      .eq('id', product.id);

    setSaving(false);

    if (error) {
      toast({
        title: 'Save failed',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    await onSaved();
    toast({
      title: 'Descriptions updated',
      description: 'Store copy has been saved.',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Store description</h3>
          <p className="text-sm text-muted-foreground">Keep the headline concise and provide richer detail below.</p>
        </div>
        <Button type="button" onClick={() => void handleSave()} disabled={disabled || saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save changes
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="market-short-description">Short description</Label>
        <Textarea
          id="market-short-description"
          rows={3}
          value={shortDescription}
          onChange={(event) => setShortDescription(event.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="market-long-description">Long description</Label>
        <Textarea
          id="market-long-description"
          rows={14}
          value={longDescription}
          onChange={(event) => setLongDescription(event.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
